export function refreshPricingDrafts(drafts, rows) {
  const next = {};
  for (const draft of Object.values(drafts)) {
    const row = rows.find(r => r.id === draft.id);
    if (!row) throw new Error(`Draft row no longer exists: ${draft.id}. Remove it before refreshing drafts.`);
    const updated = stageRow(row, draft.changes || {}, undefined, draft.recalculate);
    if (updated) next[row.id] = updated;
  }
  return next;
}
export const fields = ['stock','baseCost','packingMargin','margin','factor','selling','mrp'];
export const labels = { stock:'Stock',baseCost:'Base purchase price',cost:'Purchase price + costs',
  packingMargin:'Internal packing margin % (current rule)',packingFactor:'Internal packing factor',
  margin:'Margin %',factor:'Proportion factor',selling:'Selling price',mrp:'MRP' };
export function livePricingValue(row, changes, field){
  if(field!=='selling')return changes[field]??row[field];
  if(changes.selling!==undefined)return changes.selling;
  if(!['margin','factor','mrp'].some(key=>changes[key]!==undefined))return row.selling;
  const cost=Number(row.cost),margin=Number(changes.margin??row.margin),factor=Number(changes.factor??row.factor),mrp=Number(changes.mrp??row.mrp);
  if(row.cost==null||(row.margin==null&&changes.margin==null)||![cost,margin,factor,mrp].every(Number.isFinite)||cost<=0||factor<=0||mrp<=0)return row.selling;
  return Math.min(Math.ceil(cost+cost*margin*factor/100),Math.ceil(mrp));
}
export const pricingRuleFields=field=>['packingMargin','packingFactor'].includes(field)?['packingMargin','packingFactor']:['margin','factor','selling'];
export const pricingRuleKey=(row,field)=>row[['packingMargin','packingFactor'].includes(field)?'packingRuleKey':'marginRuleKey']||row.id;
export function stageSharedPricing(rows,drafts,row,field,value){
  if(!['margin','factor','selling','packingMargin','packingFactor'].includes(field)){
    const draft=stageRow(row,{[field]:value},drafts[row.id]);const next={...drafts};if(draft)next[row.id]=draft;else delete next[row.id];return next;
  }
  const keys=pricingRuleFields(field),key=pricingRuleKey(row,field),next={...drafts},combined={};
  for(const peer of rows.filter(r=>pricingRuleKey(r,field)===key)){
    const old=next[peer.id];if(!old)continue;
    const changes={...old.changes};
    for(const name of keys){if(changes[name]!==undefined)combined[name]=changes[name];delete changes[name];}
    if(Object.keys(changes).length||old.recalculate)next[peer.id]={...old,changes};else delete next[peer.id];
  }
  if(field==='selling'){delete combined.margin;delete combined.factor;}
  else if(['margin','factor'].includes(field))delete combined.selling;
  const draft=stageRow(row,{...combined,[field]:value},next[row.id]);
  if(draft)next[row.id]=draft;
  return next;
}
export function sharedPricingChanges(rows,drafts,row){
  const result={...drafts[row.id]?.changes};
  for(const field of ['margin','packingMargin']){
    const peer=rows.find(r=>pricingRuleKey(r,field)===pricingRuleKey(row,field)&&pricingRuleFields(field).some(k=>drafts[r.id]?.changes[k]!==undefined));
    if(!peer)continue;
    const changes=drafts[peer.id].changes;
    for(const key of pricingRuleFields(field))if(changes[key]!==undefined)result[key]=changes[key];
    if(field==='margin'&&changes.selling!==undefined&&peer.id!==row.id){
      delete result.selling;
      const cost=Number(peer.cost),factor=Number(changes.factor??peer.factor);
      if(cost>0&&factor>0){result.factor=factor;result.margin=Math.floor(Math.max(0,((changes.selling-0.00001)/cost-1)*100/factor)*1000)/1000;}
    }
  }
  return result;
}
const packMeasure=row=>{
  const unit=String(row.unit||'').toLowerCase().replace(/[.\s]/g,'');
  const units={kg:['weight',1000],kgs:['weight',1000],g:['weight',1],gm:['weight',1],gms:['weight',1],gram:['weight',1],grams:['weight',1],ltr:['volume',1000],ltrs:['volume',1000],l:['volume',1000],ml:['volume',1],mls:['volume',1]};
  const [dimension,multiplier]=units[unit]||[unit,1];
  return {dimension,amount:Number(row.quantity)*multiplier};
};
export const otherPackRows=(row,rows)=>row?.family?rows.filter(r=>r.family===row.family&&r.scope===row.scope&&r.pack!==row.pack):[];
export function stageOtherPacks(source,sourceDraft,targets,drafts){
  const next={...drafts};
  const changes=Object.fromEntries(Object.entries(sourceDraft?.changes||{}).filter(([key])=>key!=='stock'));
  if(!Object.keys(changes).length)throw new Error('Change a pricing field to apply it to other pack sizes. Stock balances are entered separately.');
  for(const target of targets){
    if(!otherPackRows(source,[target]).length)throw new Error('Select another pack of the same product, brand and location');
    const values={};
    for(const [field,value] of Object.entries(changes)){
      if(!target.editable.includes(field))throw new Error(`${target.size}: ${labels[field]} cannot be changed here`);
      if(['baseCost','mrp','selling'].includes(field)){
        const from=packMeasure(source),to=packMeasure(target);
        if(!from.dimension||from.dimension!==to.dimension||!(from.amount>0&&to.amount>0))throw new Error(`${target.size}: pack units cannot be converted`);
        const scaled=value*to.amount/from.amount;
        values[field]=field==='baseCost'?Math.round(scaled*100)/100:Math.ceil(scaled-1e-9);
      }else values[field]=value;
      if(next[target.id]?.changes[field]!==undefined&&next[target.id].changes[field]!==values[field])throw new Error(`${target.size}: already has a different draft for ${labels[field]}. Review it first.`);
    }
    const staged=stageRow(target,values,next[target.id]);
    if(staged)next[target.id]=staged;
  }
  return next;
}
export function stageRow(row, values, previous, recalculate = false) {
  const changes = { ...previous?.changes };
  for (const field of fields) {
    if (!(field in values)) continue;
    const value = values[field];
    if (value === '' || value == null || !Number.isFinite(Number(value)) || Number(value)<0) throw new Error(`${labels[field]} must be a non-negative number`);
    if (!row.editable.includes(field) && Number(value)!==row[field]) throw new Error(`${labels[field]} must be changed in the warehouse source`);
    const n=Number(value),original=previous?.before?.[field] ?? row[field];
    if(n===original)delete changes[field];else changes[field]=n;
  }
  if ('selling' in changes && ('margin' in changes || 'factor' in changes)) throw new Error('Change selling price OR margin/factor in one draft');
  if (!Object.keys(changes).length && !recalculate && !previous?.recalculate) return null;
  return { id:row.id,name:row.name,scope:row.scope,size:row.size,before:previous?.before || Object.fromEntries(fields.map(f=>[f,row[f]])),
    pricingVersion:previous?.pricingVersion || row.pricingVersion,changes,recalculate:recalculate || previous?.recalculate || false };
}

export async function exportPricing(rows, scopes) {
  const XLSX=await import('xlsx');
  const columns=['row_id','entity_type','location','product','brand','category','pack','barcode',...fields,'cost','source'];
  const data=rows.map(r=>({row_id:r.id,entity_type:scopes.find(s=>s.id===r.scope)?.type||r.scope.split(':')[0],location:scopes.find(s=>s.id===r.scope)?.name||r.scope,product:r.name,brand:r.brand,category:r.category,pack:r.size,barcode:r.barcode,
    ...Object.fromEntries(fields.map(f=>[f,r[f]])),cost:r.cost,packingMargin:r.packingMargin,source:r.sourceNote}));
  const wb=XLSX.utils.book_new();
  const sheet=XLSX.utils.json_to_sheet(data,{header:columns});
  sheet['!cols']=columns.map(k=>({wch:['product','source'].includes(k)?42:k==='row_id'?30:20}));
  XLSX.utils.book_append_sheet(wb,sheet,'Prices & Stock');
  XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet(rows.map(r=>({row_id:r.id,pricingVersion:r.pricingVersion,values:JSON.stringify(Object.fromEntries(fields.map(f=>[f,r[f]])))}))),'Original values');
  XLSX.utils.book_append_sheet(wb,XLSX.utils.aoa_to_sheet([
    ['Edit stock, baseCost (warehouse only), margin, factor, selling, or mrp in Prices & Stock.'],
    ['Change selling OR margin/factor per row. Percentages are numeric points: enter 9 for 9%.'],
    ['Purchase price + costs includes allocated costs; edit baseCost in the warehouse to change the purchase source.'],
    ['Do not change row_id, location, pack, reference columns, or Original values. Upload stages drafts only.'],
    ['Internal packing columns show current configuration, not historical charges. Edit them on warehouse rows.'],
    ['Vadaparru stock and MRP must be edited in the warehouse source.'],
  ]),'Instructions');
  XLSX.writeFile(wb,`pricing-stock-${new Date().toISOString().slice(0,10)}.xlsx`);
}

export function planPricingImport(data, baseline, currentRows, drafts) {
  const seen=new Set(),next={...drafts};
  for(const [index,item] of data.entries()){
    const id=String(item.row_id||'');
    if(seen.has(id))throw new Error(`Excel row ${index+2}: duplicate row ID`);seen.add(id);
    const original=baseline.find(b=>b.row_id===id),row=currentRows.find(r=>r.id===id);
    if(!original||!row)throw new Error(`Excel row ${index+2}: unknown row. Export a fresh workbook.`);
    let before;try{before=JSON.parse(original.values);}catch{throw new Error('Original values sheet is invalid');}
    const changes={};
    for(const f of fields){
      const v=item[f];
      if(row[f]==null && (v==null||v===''))continue;
      if(v==null||v===''||typeof v==='boolean'||!Number.isFinite(Number(v))||Number(v)<0)throw new Error(`Excel row ${index+2}: invalid ${labels[f]}`);
      if(Number(v)!==before[f]){
        if(row[f]!==before[f] || row.pricingVersion!==original.pricingVersion)throw new Error(`Excel row ${index+2}: saved values changed; export and review again`);
        if(!row.editable.includes(f))throw new Error(`Excel row ${index+2}: ${labels[f]} is read-only for this location`);
        changes[f]=Number(v);
      }
    }
    for(const f of ['cost'])if(item[f]!==''&&item[f]!=null&&Number(item[f])!==row[f])throw new Error(`Excel row ${index+2}: ${labels[f]} is a reference column`);
    if(Object.keys(changes).length){const staged=stageRow(row,changes,next[id]);if(staged)next[id]=staged;else delete next[id];}
  }
  return next;
}

export async function importPricing(file,rows,drafts){
  if(!/\.xlsx$/i.test(file.name)||file.size>5*1024*1024)throw new Error('Upload the exported .xlsx workbook, up to 5 MB');
  const XLSX=await import('xlsx');const wb=XLSX.read(await file.arrayBuffer(),{type:'array'});
  for(const name of ['Prices & Stock','Original values'])if(!wb.Sheets[name])throw new Error(`Missing ${name} sheet. Use Export Excel first.`);
  const sheet=wb.Sheets['Prices & Stock'];
  for(const [key,cell]of Object.entries(sheet))if(!key.startsWith('!')&&cell.f)throw new Error('Replace formulas with their values before uploading');
  return planPricingImport(XLSX.utils.sheet_to_json(sheet,{defval:''}),XLSX.utils.sheet_to_json(wb.Sheets['Original values'],{defval:''}),rows,drafts);
}
